import React from 'react'
import { isNull } from 'lodash'
import {
  SortDirectionEnum,
  TestsFull_tests_pagination,
  TestLocations_testLocations,
} from '@modmd/data'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { Box } from 'components/Layout'

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  inputData: TestLocations_testLocations[]
  pagination?: TestsFull_tests_pagination
  sorter?: DataListProps['sorter']
  requestDebounceInMs?: number
  isLoading: boolean
  pageLength?: number
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onPageChange?: (page: number) => void
  onRowClick?: (data: TestLocations_testLocations) => void
  handleDelete: (
    item: TestLocations_testLocations,
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => void
  handleEdit: (
    item: TestLocations_testLocations,
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => void
}

const defaultColumnLabels = {
  name: 'Name',
  state: 'State',
  actions: 'Actions',
}

const PAGE_LENGTH = 10

export const LocationDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  inputData,
  sorter,
  pagination,
  isLoading,
  onSortChange,
  onPageChange,
  onRowClick,
  pageLength = PAGE_LENGTH,
  handleDelete,
  handleEdit,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const columnLabels = { ...defaultColumnLabels }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = '2fr 1fr 5rem'

  return (
    <>
      <Card>
        <Datalist
          header={tableHeader}
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={inputData}
          sorter={sorter}
          onSortChange={onSortChange}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={() => (
            <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
              {!isNull(columnLabels.name) ? (
                <Datalist.HeaderCell label={columnLabels.name} />
              ) : null}
              {!isNull(columnLabels.state) ? (
                <Datalist.HeaderCell label={columnLabels.state} />
              ) : null}
              {!isNull(columnLabels.actions) ? (
                <Datalist.HeaderCell label={columnLabels.actions} />
              ) : null}
            </Datalist.Row>
          )}
          renderTableRow={(data) => {
            const { name, state, deleted, isDefaultPrice } = data
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(data)
                      }
                    : undefined
                }
                {...(!isDesktop ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.name) ? (
                  <Datalist.Cell label={columnLabels.name}>{name}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.state) ? (
                  <Datalist.Cell label={columnLabels.state}>{state}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.actions) ? (
                  <Datalist.Cell label={columnLabels.actions}>
                    <Box flexDirection="row">
                      <Button
                        disabled={false}
                        appearance="ghost"
                        colorVariant="info"
                        size="small"
                        onClick={(e) => {
                          handleEdit(data, e)
                        }}
                      >
                        <Icon.Edit />
                      </Button>
                      {deleted ? (
                        <Button
                          disabled={false}
                          appearance="ghost"
                          colorVariant="success"
                          size="small"
                          onClick={(e) => {
                            handleDelete(data, e)
                          }}
                        >
                          <Icon.Checkmark />
                        </Button>
                      ) : (
                        <Button
                          disabled={isDefaultPrice}
                          appearance="ghost"
                          colorVariant="danger"
                          size="small"
                          onClick={(e) => {
                            handleDelete(data, e)
                          }}
                        >
                          <Icon.Trash />
                        </Button>
                      )}
                    </Box>
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
          footer={tableFooter}
        />
      </Card>
      {pagination && pagination.total > pagination.length && onPageChange && (
        <Pagination.Wrapper>
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(pagination.total / pagination.length) || 1}
            currentPage={Math.ceil(pagination.from / pagination.length) + 1}
            onPageChange={onPageChange}
          />
        </Pagination.Wrapper>
      )}
    </>
  )
}
